import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import { FormattedMessage } from "react-intl";
import Select from "react-select";
import CreateableSelect from "react-select/creatable";
import { useParams } from "react-router";

import {
  Module,
  ModuleBody,
  ModuleHeader,
  Wrapper,
  TextInput,
  Button,
  InlineLoading,
  colors,
} from "@wfp/ui";
import { faChevronLeft } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

import { countriesDuck } from "data-handler/ducks/countries";
import { getCurrentUser } from "data-handler/ducks/auth";
import {
  getVendorCategories,
  getCurrentAdminVendor,
  getIsVendorCategoriesFetching,
  requestVendorCategories,
  requestCurrentAdminVendor,
  createVendor,
  editVendor,
} from "data-handler/ducks/vendors";
import {
  requestCountryAdminLevels,
  requestSchoolAdmins,
  getCountryAdminLevels,
  getSchoolAdmins,
} from "data-handler/ducks/adminLevels";
import { selectContainerStyles } from "SCConstants";
import ConfirmationModal from "components/ConfirmationModal";
import { getCreateAdminFields } from "components/CountryAdmin/CountryAdminContent/CountryAdminSchools/CountryAdminSchoolPage/utils";

const notAvailableOption = { label: "N/A", value: null };

const CountryAdminVendorPage = ({ create }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { vendorId } = useParams();
  const [confirmationModalState, setConfirmationModalState] = useState(false);
  const [adminLevelErrorState, setAdminLevelErrorState] = useState(false);
  const submitBtnRef = useRef();
  const isEditFormInitializedRef = useRef(false);

  const methods = useForm();
  const {
    handleSubmit,
    register,
    errors,
    control,
    setValue,
    watch,
    getValues,
  } = methods;

  const currentUser = useSelector(getCurrentUser);
  const countries = useSelector(countriesDuck.getList);
  const countriesIsFetching = useSelector(countriesDuck.isFetching());
  const vendorCategories = useSelector(getVendorCategories);
  const currentVendor = useSelector(getCurrentAdminVendor);
  const fetching = useSelector(getIsVendorCategoriesFetching);
  const countryAdminLevels = useSelector(getCountryAdminLevels);
  const schoolAdmins = useSelector(getSchoolAdmins);

  const adminFields = countryAdminLevels
    .map((item) => "adm_" + item.level)
    .sort();

  const setAdminsValue = (value, parent) => {
    const parentIndex = parent ? adminFields.indexOf(parent) : -1;
    adminFields.slice(parentIndex + 1).forEach((item) => {
      if (value === "matchingValue") {
        setValue(
          item,
          currentVendor?.[item]
            ? {
                value: currentVendor[item].id,
                label: currentVendor[item].name,
              }
            : null
        );
      } else {
        setValue(item, value);
      }
    });
  };

  const selectedCountry = watch("country");
  const selectedCountryId = selectedCountry?.value;
  const isCurrentVendorMatched =
    !create && currentVendor
      ? String(currentVendor.id) === String(vendorId)
      : false;

  useEffect(() => {
    dispatch(requestVendorCategories());
  }, [dispatch]);

  useEffect(() => {
    if (!create && vendorId) {
      dispatch(requestCurrentAdminVendor(vendorId, history));
    }
  }, [create, vendorId, dispatch, history]);

  useEffect(() => {
    isEditFormInitializedRef.current = false;
  }, [create, vendorId]);

  useEffect(() => {
    if (
      currentUser.is_admin &&
      countries.length === 0 &&
      !countriesIsFetching
    ) {
      dispatch(countriesDuck.fetchList());
    }
  }, [currentUser, countries, countriesIsFetching, dispatch]);

  const countryOptions = useMemo(
    () =>
      (countries || []).map((country) => ({
        label: country.name,
        value: country.id,
      })),
    [countries]
  );

  const vendorCategoryOptions = useMemo(
    () =>
      (vendorCategories.ids || []).map((id) => ({
        label: vendorCategories.results[id].name,
        value: vendorCategories.results[id].id,
      })),
    [vendorCategories]
  );

  const hasRequiredAdminLevels = useMemo(
    () =>
      countryAdminLevels.some((level) => level.level === 1) &&
      countryAdminLevels.some((level) => level.level === 4),
    [countryAdminLevels]
  );

  useEffect(() => {
    if (!selectedCountryId && adminLevelErrorState) {
      setAdminLevelErrorState(false);
    }
  }, [selectedCountryId, adminLevelErrorState]);

  useEffect(() => {
    if (selectedCountryId) {
      dispatch(requestCountryAdminLevels(selectedCountryId));
      dispatch(requestSchoolAdmins(1, selectedCountryId));

      const shouldPrefillAdmins =
        !create &&
        currentVendor &&
        selectedCountryId === currentVendor?.country?.id;

      if (shouldPrefillAdmins) {
        const vendorAdminFields = Object.keys(currentVendor).filter((key) =>
          key.startsWith("adm_")
        );
        for (
          let adminField = 0;
          adminField < vendorAdminFields.length - 1;
          adminField++
        ) {
          const value = vendorAdminFields[adminField];
          if (currentVendor[value]) {
            const id = currentVendor[value].id;
            const nextAdminLevelIndex = vendorAdminFields.indexOf(value) + 1;
            const nextAdminLevel = vendorAdminFields[nextAdminLevelIndex].split(
              "_"
            )[1];
            dispatch(
              requestSchoolAdmins(nextAdminLevel, selectedCountryId, id)
            );
          }
        }
        setAdminsValue("matchingValue");
      } else {
        setAdminsValue(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [create, selectedCountryId, currentVendor, dispatch]);

  function requestSchoolAdminsChildren(field, fieldname) {
    if (field && adminFields.indexOf(fieldname) !== adminFields.length - 1) {
      const nextAdminLevelIndex = adminFields.indexOf(fieldname) + 1;
      const nextAdminLevel = adminFields[nextAdminLevelIndex].split("_")[1];
      if (!field.__isNew__) {
        dispatch(
          requestSchoolAdmins(nextAdminLevel, selectedCountryId, field.value)
        );
      } else {
        dispatch(requestSchoolAdmins(nextAdminLevel, selectedCountryId));
      }
      setAdminsValue(null, fieldname);
    }
  }

  function adminFieldChange(fieldname) {
    requestSchoolAdminsChildren(getValues()[fieldname], fieldname);
  }

  useEffect(() => {
    if (
      create ||
      !currentVendor ||
      !isCurrentVendorMatched ||
      isEditFormInitializedRef.current
    ) {
      return;
    }

    const selectedCategory = vendorCategoryOptions.find(
      (option) => option.value === currentVendor?.vendor_category?.id
    );
    const selectedCountry =
      countryOptions.find(
        (option) => option.value === currentVendor?.country?.id
      ) ||
      (currentVendor?.country
        ? {
            value: currentVendor.country.id,
            label: currentVendor.country.name,
          }
        : null);

    setValue("name", currentVendor.name || "");
    setValue("ref_number", currentVendor.ref_number || "");
    setValue(
      "vendor_category",
      selectedCategory ||
        (currentVendor?.vendor_category
          ? {
              value: currentVendor.vendor_category.id,
              label: currentVendor.vendor_category.name,
            }
          : null)
    );
    setValue("country", selectedCountry || null);

    isEditFormInitializedRef.current = true;
  }, [
    create,
    currentVendor,
    vendorCategoryOptions,
    countryOptions,
    setValue,
    isCurrentVendorMatched,
  ]);

  const onSubmit = (formData) => {
    if (!hasRequiredAdminLevels) {
      setAdminLevelErrorState(true);
      return;
    }

    if (adminLevelErrorState) {
      setAdminLevelErrorState(false);
    }

    let submissionData = {};

    const adminLevelFields = adminFields
      .filter((fieldName) => formData[fieldName] && formData[fieldName].value)
      .map((fieldName) => {
        const parentIndex = adminFields.indexOf(fieldName) - 1;
        const parent = adminFields[parentIndex];
        return {
          id: formData[fieldName].value,
          parent:
            parseInt(fieldName.split("_")[1]) === 1
              ? null
              : formData["adm_" + parseInt(parent.split("_")[1])]?.value,
          level: parseInt(fieldName.split("_")[1]),
          name: formData[fieldName].label,
          isNew: formData[fieldName].__isNew__,
        };
      });

    const existingAdmins = adminLevelFields.filter(
      (item) => item.isNew !== true
    );
    existingAdmins.forEach((item) => {
      submissionData[`adm_${item.level}`] = item.id;
    });

    const createdAdmins = adminLevelFields.filter(
      (item) => item.isNew === true
    );
    if (createdAdmins && createdAdmins.length > 0) {
      submissionData["adms_for_create"] = getCreateAdminFields(
        createdAdmins,
        countryAdminLevels
      );
    }

    submissionData = {
      name: formData.name,
      ref_number: formData.ref_number,
      vendor_category: formData.vendor_category?.value,
      country: formData.country?.value,
      ...submissionData,
    };

    if (create) {
      dispatch(createVendor(submissionData, history));
    } else {
      dispatch(editVendor(submissionData, vendorId, history));
    }
  };

  const openConfirmationModal = () => {
    setConfirmationModalState(true);
  };

  const onConfirmationModalSubmit = () => {
    setConfirmationModalState(false);
    submitBtnRef.current.click();
  };

  const invalidAdminLevelLabel = (
    <FormattedMessage
      id="countryAdmin.invalidAdminLevel"
      defaultMessage="Admin Level 1 and 4 is required, Please configure Admin levels for your country within the country profile"
    />
  );

  const countryDefaultValue = currentUser?.country
    ? {
        value: currentUser.country.id,
        label: currentUser.country.name,
      }
    : null;

  if (!create && (!currentVendor || !isCurrentVendorMatched || fetching)) {
    return (
      <div className="inlineLoading">
        <InlineLoading />
      </div>
    );
  }

  return (
    <div className="admin-vendor-page">
      <Wrapper
        pageWidth="sm"
        spacing="md"
        background="lighter"
        className="table"
      >
        <Module>
          <ModuleHeader className="country-admin-school-form-row">
            <Link
              exact="true"
              to="/countryAdmin/vendors/allVendors"
              style={{ paddingRight: "10px" }}
            >
              <FontAwesomeIcon icon={faChevronLeft} size="lg" />
            </Link>
            <FormattedMessage
              id={create ? "countryAdmin.newVendor" : "countryAdmin.editVendor"}
              defaultMessage={create ? "New vendor" : "Edit vendor"}
            />
          </ModuleHeader>
          <ModuleBody>
            <div className="country-admin-school-form">
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="country-admin-school-form-row">
                  <label className="wfp--label">
                    <FormattedMessage
                      id="countryAdmin.vendorType"
                      defaultMessage="Vendor type"
                    />
                  </label>
                  <Controller
                    render={(props) => (
                      <Select
                        styles={
                          errors.vendor_category
                            ? selectContainerStyles.invalid
                            : undefined
                        }
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={vendorCategoryOptions}
                        value={props.value}
                        onChange={(value) => props.onChange(value)}
                      />
                    )}
                    name="vendor_category"
                    control={control}
                    defaultValue={null}
                    rules={{ required: true }}
                  />
                </div>

                <div className="country-admin-school-form-row">
                  <TextInput
                    name="name"
                    id="name"
                    labelText={
                      <FormattedMessage
                        id="countryAdmin.vendorName"
                        defaultMessage="Vendor name"
                      />
                    }
                    inputRef={register({ required: true })}
                  />
                </div>

                <div className="country-admin-school-form-row">
                  <TextInput
                    name="ref_number"
                    id="ref_number"
                    labelText={
                      <FormattedMessage
                        id="countryAdmin.refNo"
                        defaultMessage="Reference no."
                      />
                    }
                    inputRef={register({ required: true })}
                  />
                </div>

                <div className="country-admin-school-form-row">
                  <label
                    className="wfp--label"
                    style={{ opacity: currentUser.is_admin ? "" : "0.5" }}
                  >
                    <FormattedMessage
                      id="CountryAdmin.country"
                      defaultMessage="Country"
                    />
                  </label>
                  <Controller
                    render={(props) => (
                      <Select
                        styles={
                          errors.country
                            ? selectContainerStyles.invalid
                            : undefined
                        }
                        className="react-select-container"
                        classNamePrefix="react-select"
                        options={countryOptions}
                        value={props.value}
                        isDisabled={!currentUser.is_admin}
                        onChange={(value) => props.onChange(value)}
                      />
                    )}
                    name="country"
                    control={control}
                    defaultValue={create ? countryDefaultValue : null}
                    rules={{ required: true }}
                  />
                </div>

                {selectedCountry &&
                  countryAdminLevels.map((item) => {
                    const fieldname = "adm_" + item.level;
                    const previousAdminLevelIndex =
                      adminFields.indexOf(fieldname) - 1;
                    const previousAdminLevel =
                      adminFields[previousAdminLevelIndex];
                    const disabled = () => {
                      if (!selectedCountry) {
                        return true;
                      }
                      return item.level === 1
                        ? false
                        : getValues()[previousAdminLevel]
                        ? false
                        : true;
                    };

                    return (
                      <div
                        key={fieldname}
                        className="country-admin-school-form-row"
                      >
                        <label
                          className="wfp--label"
                          style={{ opacity: disabled() ? "0.5" : "" }}
                        >
                          {item.name}
                        </label>
                        <Controller
                          render={(props) => (
                            <CreateableSelect
                              styles={
                                errors[fieldname]
                                  ? selectContainerStyles.invalid
                                  : undefined
                              }
                              className="react-select-container"
                              classNamePrefix="react-select"
                              defaultValue={
                                !create &&
                                selectedCountry?.value ===
                                  currentVendor?.country?.id &&
                                currentVendor
                                  ? currentVendor[fieldname]
                                    ? {
                                        value: currentVendor[fieldname].id,
                                        label: currentVendor[fieldname].name,
                                      }
                                    : null
                                  : null
                              }
                              options={
                                getValues() &&
                                getValues()[previousAdminLevel]?.__isNew__
                                  ? []
                                  : [
                                      notAvailableOption,
                                      ...(schoolAdmins[item.level]?.map(
                                        (admin) => ({
                                          label: admin.name,
                                          value: admin.id,
                                        })
                                      ) || []),
                                    ]
                              }
                              isMulti={false}
                              isDisabled={disabled()}
                              value={props.value}
                              onChange={(value) => {
                                props.onChange(value);
                                adminFieldChange(fieldname);
                              }}
                            />
                          )}
                          name={fieldname}
                          control={control}
                          defaultValue={
                            !create &&
                            selectedCountry?.value ===
                              currentVendor?.country?.id &&
                            currentVendor
                              ? currentVendor[fieldname]
                                ? {
                                    value: currentVendor[fieldname].id,
                                    label: currentVendor[fieldname].name,
                                  }
                                : null
                              : null
                          }
                          rules={{ required: false }}
                        />
                      </div>
                    );
                  })}

                <div
                  className="country-admin-school-form-row"
                  style={{ color: colors["support-01"].hex }}
                >
                  {Object.keys(errors).length === 0 ? (
                    ""
                  ) : (
                    <FormattedMessage
                      id="countryAdmin.vendorProfileErrors"
                      defaultMessage="Please enter a valid value within the highlighted fields"
                    />
                  )}
                  <div className="admin-levels-error">
                    {adminLevelErrorState && invalidAdminLevelLabel}
                  </div>
                </div>

                <div className="country-admin-submit-button">
                  <Button kind="primary" onClick={openConfirmationModal}>
                    <FormattedMessage
                      id="Report.actionSubmit"
                      defaultMessage="Submit"
                    />
                  </Button>
                  <button
                    type="submit"
                    className="hidden-btn"
                    ref={submitBtnRef}
                  />
                </div>

                <ConfirmationModal
                  confirmationModalState={confirmationModalState}
                  modalType={"submit"}
                  setConfirmationModalState={setConfirmationModalState}
                  onConfirmationModalSubmit={onConfirmationModalSubmit}
                  submitMessage={
                    <FormattedMessage
                      id="countyAdmin.vendorProfileSubmit"
                      defaultMessage="Are you sure you want to submit the data of {name}?"
                      values={{
                        name:
                          watch("name") || currentVendor?.name ? (
                            watch("name") || currentVendor?.name
                          ) : (
                            <FormattedMessage
                              id="countyAdmin.newVendor"
                              defaultMessage="this new vendor"
                            />
                          ),
                      }}
                    />
                  }
                />
              </form>
            </div>
          </ModuleBody>
        </Module>
      </Wrapper>
    </div>
  );
};

export default CountryAdminVendorPage;
